import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {ResizeSeparator, ResizeSeparatorSidesEnum} from '../../../components/common/resizeSeparator';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {getCompanyHasAdvancedAnalytics} from '../../../store/data/company/companySelectors';
import {setIsSidePanelOpenAction} from '../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsDetailsError,
  getIsConversationDetailsSidePanelOpen,
  getIsSidePanelOpen,
} from '../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {ReportErrorBanner} from '../reportElements/banners/reportErrorBanner';
import {ConversationDetailsHeader} from './conversationDetailsHeader/conversationDetailsHeader';
import {ReportSidePanelHeader} from './reportSidePanelHeader';
import {renderReportSidePanelCloseButton} from './reportSidePanelHelpers';
import {ReportSidePanelUpsellOverlay} from './reportSidePanelUpsellOverlay';
import {ConversationsSection} from './sections/conversations/conversationsSection';
import {SummarySection} from './sections/summary/summarySection';
import {TimelineSection} from './sections/timeline/timelineSection';

/*
 * Const.
 */

const INITIAL_PANEL_WIDTH = 350;
const MIN_PANEL_WIDTH = 270;
const MAX_PANEL_WIDTH = 400;
const RESIZER_WIDTH = 1;

const TRANSITION_PERIOD = '0.2s';
const TRANSITION_TIMING_FUNCTION = 'ease-in-out';

/*
 * Props.
 */

interface ReportSidePanelProps {
  isVisible: boolean;
}

/*
 * Style.
 */

const StyledHeaderWrapper = styled.div`
  ${flexCenter}
  justify-content: flex-end;
`;

const StyledSidePanelWrapper = styled.div<{$isOpen: boolean; $width: number}>`
  width: ${(p) => `${p.$width}px`};
  height: 100%;
  background-color: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0px 1px 9px rgba(0, 0, 0, 0.08)`)};
  position: absolute;
  overflow: hidden;
  right: 0;

  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 0.1px 1.1fr;
  grid-auto-rows: 1fr;

  visibility: ${(p) => (p.$isOpen ? 'visible' : 'hidden')};
  transform: translateX(${(p) => (p.$isOpen ? 0 : `${p.$width}px`)});
  transition: transform ${TRANSITION_PERIOD} ${TRANSITION_TIMING_FUNCTION},
    visibility 0s ${(p) => (p.$isOpen ? '0s' : `${TRANSITION_PERIOD}`)};
`;

const StyledSidePanelContent = styled.div<{$isScrollable: boolean}>`
  grid-column: 2;
  min-height: 0;
  overflow-y: ${(p) => (p.$isScrollable ? 'auto' : 'hidden')};
`;

const StyledErrorBannerWrapper = styled.div`
  margin-top: 10px;
  padding: 0 16px;
`;

/*
 * Component.
 */

export const ReportSidePanel: FC<ReportSidePanelProps> = ({isVisible}) => {
  const dispatch = useAppDispatch();

  const onRequestClose = () => {
    dispatch(setIsSidePanelOpenAction(false));
  };

  const isSidePanelOpen = useAppSelector(getIsSidePanelOpen);
  const error = useAppSelector(getAnalyticsDetailsError);
  const hasUpsell = !useAppSelector(getCompanyHasAdvancedAnalytics);
  const isSidePanelConversationDetailsOpen = useAppSelector(getIsConversationDetailsSidePanelOpen);

  const [panelWidth, setPanelWidth] = useState(INITIAL_PANEL_WIDTH);

  return (
    <StyledSidePanelWrapper $isOpen={isVisible && isSidePanelOpen} $width={panelWidth + RESIZER_WIDTH}>
      <ResizeSeparator
        column={RESIZER_WIDTH}
        size={panelWidth}
        minSize={MIN_PANEL_WIDTH}
        maxSize={MAX_PANEL_WIDTH}
        onSizeChange={setPanelWidth}
        side={ResizeSeparatorSidesEnum.LEFT}
      />
      {maybeRenderError(onRequestClose, error)}
      {!error && renderSidePanelConversationList(hasUpsell)}
      {!error && renderSidePanelConversationDetails(isSidePanelConversationDetailsOpen, panelWidth)}
      {hasUpsell && <ReportSidePanelUpsellOverlay />}
    </StyledSidePanelWrapper>
  );
};

/*
 * Helpers.
 */

function renderSidePanelConversationList(hasUpsell: boolean) {
  return (
    <StyledSidePanelContent $isScrollable={!hasUpsell}>
      <ReportSidePanelHeader />
      <ConversationsSection hasUpsell={hasUpsell} />
    </StyledSidePanelContent>
  );
}

function renderSidePanelConversationDetails(isOpen: boolean, panelWidth: number) {
  return (
    <StyledSidePanelWrapper $isOpen={isOpen} $width={panelWidth}>
      <StyledSidePanelContent $isScrollable>
        <ConversationDetailsHeader />
        <SummarySection />
        <TimelineSection />
      </StyledSidePanelContent>
    </StyledSidePanelWrapper>
  );
}

function maybeRenderError(onRequestClose: Handler, error?: string) {
  if (!error) {
    return null;
  }

  return (
    <StyledErrorBannerWrapper>
      <StyledHeaderWrapper>{renderReportSidePanelCloseButton(onRequestClose)}</StyledHeaderWrapper>
      <ReportErrorBanner message={error} />
    </StyledErrorBannerWrapper>
  );
}
